export function detectBrowserPreferences() {
  // Guess unit system based on locale
  const locale = navigator.language || 'en-US';
  const imperialLocales = ['en-US', 'en-LR', 'en-MM', 'es-US'];
  const units: 'metric' | 'imperial' = imperialLocales.includes(locale) ? 'imperial' : 'metric';

  // Detect preferred date format
  let dateFormat = 'YYYY-MM-DD';
  try {
    const testDate = new Date(2020, 11, 31); // Dec 31, 2020
    const formatted = new Intl.DateTimeFormat().format(testDate);
    if (formatted.startsWith('2020')) {
      dateFormat = 'YYYY-MM-DD';
    } else if (formatted.startsWith('12')) {
      dateFormat = 'MM/DD/YYYY';
    } else if (formatted.startsWith('31')) {
      dateFormat = 'DD/MM/YYYY';
    } else {
      // Fallback based on separator
      if (formatted.indexOf('.') !== -1) {
        dateFormat = 'DD.MM.YYYY';
      } else if (formatted.indexOf('/') !== -1) {
        dateFormat = 'MM/DD/YYYY';
      }
    }
  } catch (e) {
    console.warn('Failed to detect browser date format:', e);
  }

  // Detect preferred time format (12h vs 24h)
  let timeFormat: '12h' | '24h' = '24h';
  try {
    const testTime = new Date(2020, 11, 31, 16, 0, 0); // 4:00 PM
    const formattedTime = new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).format(testTime);
    if (
      formattedTime.includes('PM') ||
      formattedTime.includes('AM') ||
      formattedTime.includes('pm') ||
      formattedTime.includes('am')
    ) {
      timeFormat = '12h';
    }
  } catch (e) {
    console.warn('Failed to detect browser time format:', e);
  }

  return { units, dateFormat, timeFormat };
}

export function formatDistance(meters: number, units: 'metric' | 'imperial' = 'metric') {
  if (units === 'imperial') {
    const miles = meters / 1609.344;
    return miles.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' mi';
  }
  const km = meters / 1000;
  return km.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' km';
}

export function formatElevation(meters: number, units: 'metric' | 'imperial' = 'metric') {
  if (units === 'imperial') {
    const feet = meters * 3.28084;
    return Math.round(feet).toLocaleString('en-US') + ' ft';
  }
  return Math.round(meters).toLocaleString('en-US') + ' m';
}

export function formatDuration(seconds: number) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  if (d > 0) {
    const formattedDays = d.toLocaleString('en-US');
    return `${formattedDays}d ${h}h ${m}m`;
  }
  if (h > 0) {
    return `${h}h ${m}m`;
  }
  return `${m}m`;
}

export function formatEta(seconds?: number | null): string {
  if (seconds === undefined || seconds === null || seconds < 0) return '--:--';
  if (seconds === 0) return '0s';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `${h}h ${m}m ${s}s`;
  }
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
}

export function formatMs(ms?: number | null): string {
  if (ms === undefined || ms === null || isNaN(ms)) return '-';
  if (ms <= 0) return '0ms';

  const totalSec = Math.floor(ms / 1000);
  const remainingMs = Math.round(ms % 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;

  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0) parts.push(`${s}s`);
  if (remainingMs > 0 || parts.length === 0) parts.push(`${remainingMs}ms`);

  return parts.join(' ');
}

export function formatDate(isoString: string, format: string) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';

  const pad = (n: number) => String(n).padStart(2, '0');
  const YYYY = date.getFullYear();
  const MM = pad(date.getMonth() + 1);
  const DD = pad(date.getDate());

  switch (format) {
    case 'DD/MM/YYYY':
      return `${DD}/${MM}/${YYYY}`;
    case 'DD.MM.YYYY':
      return `${DD}.${MM}.${YYYY}`;
    case 'MM/DD/YYYY':
      return `${MM}/${DD}/${YYYY}`;
    case 'YYYY-MM-DD':
    default:
      return `${YYYY}-${MM}-${DD}`;
  }
}

export function formatSpeedOrPace(speedMps: number, activityType: string, units: 'metric' | 'imperial') {
  if (speedMps <= 0) return '-';
  const isRunningOrHiking = ['running', 'hiking', 'climbing'].includes(activityType);

  if (isRunningOrHiking) {
    // Pace
    const metersPerMin = speedMps * 60;
    if (metersPerMin <= 1) return '-:--';

    const targetDistance = units === 'imperial' ? 1609.344 : 1000;
    const paceMin = targetDistance / metersPerMin;
    const mins = Math.floor(paceMin);
    const secs = Math.round((paceMin - mins) * 60);
    const suffix = units === 'imperial' ? '/mi' : '/km';
    return `${mins}:${String(secs).padStart(2, '0')} ${suffix}`;
  } else {
    // Speed
    const multiplier = units === 'imperial' ? 2.23694 : 3.6;
    const suffix = units === 'imperial' ? ' mph' : ' km/h';
    return `${(speedMps * multiplier).toFixed(1)}${suffix}`;
  }
}
